import _ from 'lodash';

const normalizeRateCode = (rateCode) =>
  String(rateCode ?? '')
    .trim()
    .toUpperCase();

/** Accepts numeric or numeric-string orders. Null, empty and non-numeric values mean no order. */
const parseRateCodeOrder = (order) => {
  if (typeof order === 'number') return Number.isFinite(order) ? order : null;
  if (typeof order !== 'string' || !order.trim()) return null;

  const parsedOrder = Number(order);
  return Number.isFinite(parsedOrder) ? parsedOrder : null;
};

const hasConfiguredRateCodes = (rateCodes) =>
  Array.isArray(rateCodes) && rateCodes.some((rc) => normalizeRateCode(rc?.rateCode));

/**
 * Maps normalized rate codes to their configured order. Rate codes without a valid order are left
 * out so they fall behind the ordered ones, both when hiding duplicates and when ordering rates.
 */
const createRateCodeOrderMap = (rateCodes) => {
  const rateCodeOrderMap = new Map();
  if (!Array.isArray(rateCodes)) return rateCodeOrderMap;

  rateCodes.forEach((rc) => {
    const rateCode = normalizeRateCode(rc?.rateCode);
    const order = parseRateCodeOrder(rc?.order);
    if (!rateCode || order === null || rateCodeOrderMap.has(rateCode)) return;

    rateCodeOrderMap.set(rateCode, order);
  });

  return rateCodeOrderMap;
};

const getRoomRateCodeOrder = (room, rateCodeOrderMap) =>
  rateCodeOrderMap.get(normalizeRateCode(room?.ratePlanClientId || room?.ratePlanCode)) ?? Infinity;

const getRoomDescription = (room) => room?.originalDesc || room?.desc || room?.description || '';

const getDuplicateRateKey = ({ product, room }) =>
  JSON.stringify([
    product.roomPrice?.markupPrice || 0,
    getRoomDescription(room),
    product.refundType,
    (product.ValueAdds ?? []).slice().sort().join('|'),
  ]);

const getPrioritizedProducts = (products, rateCodeOrderMap) => {
  if (!products?.length) {
    return null;
  }

  try {
    const roomInfos = products
      .filter((product) => product?.rooms)
      .flatMap((product) => product.rooms.map((room) => ({ product, room })));

    const groupedProducts = _.groupBy(roomInfos, getDuplicateRateKey);

    return Object.values(groupedProducts).map((group) => {
      const sortedRooms = _.sortBy(group, (roomInfo) =>
        getRoomRateCodeOrder(roomInfo.room, rateCodeOrderMap),
      );
      return sortedRooms[0].product;
    });
  } catch (ex) {
    console.error('An error occurred while prioritizing hotel products.', ex);
    return products;
  }
};

/** Special rates are keyed separately so they never move into the normal rates block. */
const getSameRoomTypeAndPriceKey = (product) => {
  const room = product?.rooms?.[0];
  if (!room) return null;

  return JSON.stringify([
    product.isSpecialRate ? 'special' : 'normal',
    product.roomPrice?.markupPrice || 0,
    room.type || '',
    getRoomDescription(room),
  ]);
};

/**
 * Rates with the same room type, description and price are ordered by the preferred partner rate
 * code order. Each tied rate is swapped only within the positions its group already holds, so
 * rates with a different price, room type or description keep their existing position.
 * Applies to all agencies with ordered rate codes, regardless of the duplicate rate settings.
 */
const orderSameRoomTypeAndPriceRatesByRateCode = (products, rateCodes) => {
  const rateCodeOrderMap = createRateCodeOrderMap(rateCodes);
  if (!products?.length || !rateCodeOrderMap.size) return products;

  try {
    const productPositionsByKey = new Map();
    products.forEach((product, position) => {
      const key = getSameRoomTypeAndPriceKey(product);
      if (key === null) return;

      if (!productPositionsByKey.has(key)) productPositionsByKey.set(key, []);
      productPositionsByKey.get(key).push(position);
    });

    const orderedProducts = [...products];
    productPositionsByKey.forEach((positions) => {
      if (positions.length < 2) return;

      const productsInRateCodeOrder = _.sortBy(
        positions.map((position) => products[position]),
        (product) => getRoomRateCodeOrder(product.rooms[0], rateCodeOrderMap),
      );
      positions.forEach((position, index) => {
        orderedProducts[position] = productsInRateCodeOrder[index];
      });
    });

    const isOrderChanged = orderedProducts.some(
      (product, position) => product !== products[position],
    );
    return isOrderChanged ? orderedProducts : products;
  } catch (ex) {
    console.error('An error occurred while ordering hotel rates by rate code.', ex);
    return products;
  }
};

const hideDuplicateHotelRates = (
  hotelDetails,
  travelAgency,
  hotelRateCodes,
  isEndCustomer,
  isLoggedIn,
) => {
  let doHideDuplicateHotelRates = travelAgency?.settings?.doHideDuplicateHotelRates;
  if (isEndCustomer || !isLoggedIn) {
    doHideDuplicateHotelRates =
      travelAgency?.settings?.doHideDuplicateHotelRatesCFP ||
      travelAgency?.settings?.doHideDuplicateHotelRates;
  }
  if (
    !doHideDuplicateHotelRates ||
    !hotelDetails?.hotel?.products?.length ||
    !hasConfiguredRateCodes(hotelRateCodes)
  ) {
    return hotelDetails;
  }

  const prioritizedProducts = getPrioritizedProducts(
    hotelDetails.hotel.products,
    createRateCodeOrderMap(hotelRateCodes),
  );

  if (!prioritizedProducts?.length) {
    return hotelDetails;
  }

  const updatedHotelDetails = _.cloneDeep(hotelDetails);
  updatedHotelDetails.hotel.products = prioritizedProducts;
  return updatedHotelDetails;
};

const prioritizeHotelRates = (
  hotelDetails,
  travelAgency,
  hotelRateCodes,
  isEndCustomer,
  isLoggedIn,
) => {
  if (!hotelDetails?.hotel?.products?.length) return hotelDetails;

  const deduplicatedHotelDetails = hideDuplicateHotelRates(
    hotelDetails,
    travelAgency,
    hotelRateCodes,
    isEndCustomer,
    isLoggedIn,
  );

  const { products } = deduplicatedHotelDetails.hotel;
  const orderedProducts = orderSameRoomTypeAndPriceRatesByRateCode(products, hotelRateCodes);
  if (orderedProducts === products) return deduplicatedHotelDetails;

  return {
    ...deduplicatedHotelDetails,
    hotel: { ...deduplicatedHotelDetails.hotel, products: orderedProducts },
  };
};

export default prioritizeHotelRates;
